import React from "react";
import { SketchPicker } from "react-color";
import styled from "styled-components";
import _ from "lodash";
import Select from "react-select";

// import Sources from "../externalsource/Sources";
import { getCollections } from "../spreadsheet/store";
import * as Colors from "../reusable/colors";
import { Events } from "../trigger/index";
import { InputUI, SlideShowTransitions } from "../reusable/components";
import faIcons from "./faicons";

import "./react-select.css";

const inputTypes = {
  number: [
    "width",
    "height",
    "top",
    "left",
    "minWidth",
    "minHeight",
    "maxItems",
    "space",
    "borderRadius",
    "borderBottomLeftRadius",
    "borderBottomRightRadius",
    "borderTopLeftRadius",
    "borderTopRightRadius",
    "fontSize",
    "letterSpacing",
    "fontWeight",
    "lineHeight",
    "padding",
    "borderWidth",
    "zIndex",
    "stayDuration",
    "slidingDuration",
    "idleDuration",
    "streamWidth",
    "streamHeight",
    "delay",
    "childDelay",
    "opacity",
    "perspective",
    "rotateX",
    "rotateY",
    "progress",
    "rotate",
    "skewX",
    "skewY",
    "blur",
    "limit",
    "skip",
    "maximum",
    "showEvery"
  ],
  text: ["text", "url", "fontFamily", "backgroundImage", "label", "transformOrigin", "gradient"],
  select: [
    "collection",
    "direction",
    "textAlign",
    "borderStyle",
    "fontStyle",
    "enterAnimation",
    "leaveAnimation",
    "orientation",
    "backgroundSize",
    "showWhen",
    "icon",
    "transition",
    "textOverflow"
  ],
  color: ["backgroundColor", "color", "borderColor", "backgroundColor2"],
  slide: ["slide"],
  button: ["addSlide", "resetAspectRatio"],
  checkbox: ["lockAspectRatio", "hideOverflow", "loop", "cycle"]
};

export const propertyType = p => Object.keys(inputTypes).find(i => inputTypes[i].includes(p));

const constraints = {
  fontWeight: { min: 100, max: 900, step: 100 },
  lineHeight: { step: 0.1 },
  delay: { step: 100 },
  childDelay: { step: 100 },
  width: { min: 1 },
  height: { min: 1 },
  opacity: { step: 0.05, min: 0, max: 1 },
  perspective: { min: 0, max: 1000 },
  rotate: { max: 180, min: -180 },
  rotateX: { max: 180, min: -180 },
  rotateY: { max: 180, min: -180 },
  limit: { min: 1 },
  skip: { min: 0 },
  blur: { min: 0 }
};

const selectOptions = p => {
  const opts = {
    collection: p => [""].concat(getCollections.get()),
    direction: p => ["vertical", "horizontal"],
    textAlign: p => ["center", "left", "right"],
    borderStyle: p => ["solid", "dotted", "dashed", "double", "groove", "ridge", "inset", "outset"],
    fontStyle: p => ["normal", "italic", "oblique"],
    enterAnimation: p => ["none", "elevator", "fade", "accordianVertical", "accordianHorizontal"],
    leaveAnimation: p => ["none", "elevator", "fade", "accordianVertical", "accordianHorizontal"],
    orientation: p => ["horizontal", "vertical"],
    backgroundSize: p => ["cover", "contain"],
    showWhen: p => [""].concat(_.values(Events)),
    icon: p => faIcons.map(i => i.id),
    transition: p => Object.keys(SlideShowTransitions),
    textOverflow: p => ["wrap", "ellipsis", "hidden"]
  };
  return opts[p.prop](p);
};

const getConstraint = p => v => constraints[p] && constraints[p][v];

const TextInput = props => (
  <InputUI
    border={props.boder}
    large={props.large}
    type="text"
    value={props.value}
    onChange={e => props.onChange(e.target.value)}
  />
);

const NumberInput = props => (
  <InputUI
    type="number"
    value={props.value}
    min={props.min || getConstraint(props.prop)("min")}
    max={props.max || getConstraint(props.prop)("max")}
    step={props.step || getConstraint(props.prop)("step")}
    onChange={e => props.onChange(Number(e.target.value))}
  />
);

const ColorPreview = styled.div`
  width: 25px;
  height: 25px;
  border: 1px solid #f2f2f2;
  border-radius: 4px;
`;

class ColorInput extends React.Component {
  state = {
    open: false
  };

  coverStyle = {
    position: "fixed",
    top: "0px",
    right: "0px",
    bottom: "0px",
    left: "0px"
  };

  containerStyle = { position: "absolute", top: "-20px", right: "0px", zIndex: 10 };

  relative = { position: "relative" };

  render() {
    return this.state.open ? (
      <div style={this.relative}>
        <div style={this.coverStyle} onClick={e => this.setState({ open: false })} />
        <div style={this.containerStyle}>
          <SketchPicker
            color={this.props.value}
            onChange={c =>
              this.props.onChange(`rgba(${c.rgb.r}, ${c.rgb.g}, ${c.rgb.b}, ${c.rgb.a})`)
            }
          />
        </div>
      </div>
    ) : (
      <ColorPreview
        onClick={e => this.setState({ open: true })}
        style={{
          background: `linear-gradient(${this.props.value}, ${
            this.props.value
          }), url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAIAAACQkWg2AAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAACpJREFUeNpi3LFjBwM2oK2tjVWciYFEMKqBGMCCK7yvXr06Gkr00wAQYACiEgXLqZw3aAAAAABJRU5ErkJggg==") 0% 0% / 8px`
        }}
      />
    );
  }
}

const optionRender = o => (
  <div>
    <i className={"fa fa-" + o.value} />
    {"  "}
    {o.value}
  </div>
);

export const SelectInput = props => {
  return (
    <Select
      value={props.value}
      options={props.options.map(o => ({ value: o, label: o }))}
      onChange={v => v && props.onChange(v.value)}
      clearable={false}
      style={{
        width: props.width || "100px",
        backgroundColor: Colors.primary,
        height: "30px",
        color: Colors.Text
      }}
      optionRenderer={props.prop === "icon" ? optionRender : undefined}
      searchable={props.options.length > 8}
    />
  );
};

const GenericSelect = props => (
  <SelectInput
    value={props.value || selectOptions(props)[0]}
    onChange={a => props.onChange(a)}
    options={selectOptions(props)}
    prop={props.prop}
  />
);

const CheckBoxInput = props => (
  <input type="checkbox" checked={props.value} onChange={e => props.onChange(e.target.checked)} />
);

const SlideInput = props => <NumberInput {...props} min={1} max={props.allProps.children.length} />;

const ButtonInput = props => <button onClick={props.onClick}>{props.prop}</button>;

export const inputComponent = {
  number: NumberInput,
  text: TextInput,
  select: GenericSelect,
  slide: SlideInput,
  color: ColorInput,
  button: ButtonInput,
  checkbox: CheckBoxInput,
  progress: NumberInput
};

export const propertyToInputType = prop => {
  const type = Object.keys(inputTypes).filter(k => inputTypes[k].indexOf(prop) > -1)[0];
  if (!type)
    console.warn(
      'Could not get input type for "' +
        prop +
        '". You should add this property to inputTypes in Input.js'
    );
  return inputComponent[type];
};

export const supportedProps = Object.keys(inputTypes).reduce(
  (acc, i) => acc.concat(inputTypes[i]),
  []
);



// WEBPACK FOOTER //
// ./src/properties/input.js